import React from "react";

import type { ProcessedSameEventMultiOutcome } from "@/hooks/firestore/betting/useEntries";
import type { Sport } from "@/hooks/firestore/models/sports";

import cx from "classnames";
import { useTitle } from "@/hooks/graphql/useTitles";
import { Clamp, Live } from "@picklebet/ui";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { useBetTracker } from "@/features/entries/hooks/useBetTracker";
import { deriveInterimResult } from "@/features/entries/util/bet-tracker-helpers";
import { ProgressBar } from "../bet-tracker/ProgressBar";
import { StatusIcon } from "../bet-tracker/LegStatus";
import { WinCelebration } from "../common/WinCelebration";

import * as styles from "./SameEventMultiOutcome.module.scss";

interface SameEventMultiOutcomeProps {
  outcome: ProcessedSameEventMultiOutcome;
  sport: Sport;
  isWon: boolean;
  isRace: boolean;
  silksUrl: string;
  eventId: string;
  countdown?: React.ReactNode;
  trackerDisabled?: boolean;
  status: {
    message: string;
    variant: "neutral" | "success" | "danger" | "warning";
  } | null;
}

const SameEventMultiOutcome = ({
  outcome,
  sport,
  isWon,
  isRace,
  silksUrl,
  trackerDisabled,
  eventId,
  countdown,
  status,
}: SameEventMultiOutcomeProps) => {
  const title = useTitle(sport);
  const isVoid = outcome.outcomeResult === "VOID";
  const isLive = status?.message === "LIVE";

  const [winCriteriaId, { criteria, dataType } = {}] =
    Object.entries(outcome.winCriteria ?? {})[0] ?? [];

  const value = useBetTracker(dataType, eventId, winCriteriaId);

  const interimResult = deriveInterimResult(value, criteria);

  const isExactlyZero = Number(criteria?.EQ) === 0;

  const showProgress = !!criteria && !isRace;

  const showProgressBar =
    !trackerDisabled &&
    showProgress &&
    !isExactlyZero &&
    typeof value === "number" &&
    Object.keys(criteria ?? {}).length === 1;

  const showStatusIcon =
    !trackerDisabled &&
    showProgress &&
    isLive &&
    interimResult !== "UNDECIDED" &&
    (typeof value === "string" || isExactlyZero);

  const clampText =
    isLive && interimResult !== "UNDECIDED" && outcome.winCriteria
      ? "INTERIM"
      : status?.message;

  const isWinCelebration = isWon && status?.variant === "success";

  const clamp =
    isLive && interimResult === "UNDECIDED" ? (
      <Live />
    ) : (
      <Clamp variant={isLive ? "neutral" : status?.variant}>
        <WinCelebration isEnabled={isWinCelebration}>
          {isRace && isVoid ? "SCRATCHED" : clampText}
        </WinCelebration>
      </Clamp>
    );

  return (
    <div className={styles.container}>
      <div
        className={cx(styles.outcome, {
          [styles.isWon]: isWon,
          [styles.isVoid]: isVoid,
        })}
      >
        <div className={styles.icon}>
          <TitleIcon title={title} svg />
        </div>
        <div className={styles.outcomeAndMarket}>
          <div className={styles.outcomeName}>
            <OutcomeIcon
              className={styles.pickIcon}
              outcomeType={outcome.outcomeType}
              competitor={outcome.competitor ?? undefined}
              silksUrl={silksUrl}
            />{" "}
            {outcome.outcomeName}
          </div>
          <div className={styles.marketName}>{outcome.marketName}</div>
          {showProgressBar && (
            <ProgressBar
              criteria={criteria}
              value={value}
              outcomeResult={outcome.outcomeResult}
              isLive={isLive}
            />
          )}
          {showStatusIcon && (
            <div data-animation-class="scale-in" className={styles.statusIcon}>
              <StatusIcon
                interimResult={interimResult}
                finalResult={outcome.outcomeResult}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.clamp}>{clampText ? clamp : countdown}</div>
    </div>
  );
};

export { SameEventMultiOutcome };
